<script setup>
import { Link, useForm, usePage } from '@inertiajs/vue3'
import { AlertCircle, CheckCircle2, GraduationCap } from 'lucide-vue-next'
import { computed } from 'vue'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { Button } from '@/Components/ui/button'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import { store } from '@/routes/login'

const page = usePage()
const form = useForm({
  email: '',
  password: '',
})

const submit = () => {
  form.submit(store(), {
    onSuccess: () => {
      if (form.wasSuccessful) {
        trackEvent('login', { method: 'email' })
      }
    },
  })
}

const hasErrors = computed(() => Object.keys(form.errors).length > 0)
const status = computed(() => page.props.flash.status)
const error = computed(() => page.props.flash.error)
</script>

<template>
  <AppLayout>
    <div class="flex items-center justify-center min-h-[calc(100vh-200px)] py-12 px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md bg-white rounded-2xl shadow-lg p-10">
        <!-- Header -->
        <div class="mb-8">
          <div class="flex justify-center mb-6">
            <div class="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white">
              <GraduationCap class="w-6 h-6" />
            </div>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 text-center mb-2">Welcome back</h1>
          <p class="text-gray-600 text-center">Enter your email to sign in to your account</p>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          <!-- General Error Alert -->
          <Alert v-if="hasErrors || error" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              <span v-if="error">{{ error }}</span>
              <span v-else>Please check your credentials and try again.</span>
            </AlertDescription>
          </Alert>

          <!-- Success Status Alert -->
          <Alert v-if="status" class="border-green-500 text-green-700 [&>svg]:text-green-700">
            <CheckCircle2 class="h-4 w-4" />
            <AlertDescription>{{ status }}</AlertDescription>
          </Alert>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-3">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="m@example.com"
              v-model="form.email"
              required
              autofocus
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.email ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.email" class="text-sm text-red-500 mt-1">{{ form.errors.email }}</p>
          </div>

          <!-- Password Field -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label for="password" class="block text-sm font-semibold text-gray-700">
                Password <span class="text-red-500">*</span>
              </label>
              <Link
                href="/forgot-password"
                class="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              v-model="form.password"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.password ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.password" class="text-sm text-red-500 mt-1">{{ form.errors.password }}</p>
          </div>

          <Button class="w-full bg-blue-600 hover:bg-blue-700 h-auto px-5 py-4 text-base font-semibold rounded-xl" :disabled="form.processing">
            Sign In
          </Button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center text-sm text-gray-600">
          Don't have an account?
          <Link href="/signup" class="font-semibold text-blue-600 hover:text-blue-500">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
